/* eslint-env browser */
import React, { useEffect } from 'react';
import { PrismicPreviewProvider } from 'gatsby-plugin-prismic-previews';
import markerSDK from '@marker.io/browser';
import '@fontsource-variable/inter';
import { linkResolver, htmlSerializer } from './src/utils/prismic-config';
import { LightboxProvider } from './src/common/lightbox/lightbox-provider';

function MarkerIoLoader ({ destination }) {
  useEffect(() => {
    if (!destination) return;

    markerSDK.loadWidget({
      destination
    }).then((widget) => {
      window.markerWidget = widget;
    });

    return () => {
      if (!window.markerWidget) return;
      window.markerWidget.unload();
    };
  }, [ destination ]);

  return null;
}

const repositoryConfigs = [
  {
    repositoryName: process.env.PRISMIC_REPOSITORY,
    linkResolver, htmlSerializer
  }
];

export const wrapRootElement = ({ element }) => {
  return (
    <PrismicPreviewProvider
      repositoryConfigs={repositoryConfigs}
    >

      <LightboxProvider>
        {element}
      </LightboxProvider>
    </PrismicPreviewProvider>
  );
};

export const shouldUpdateScroll = () => [ 0, 0 ];

// Something in Gatsby is breaking ScrollHelper from gatsby-react-router-scroll.
// So to keep it from working, we break scrollTo();
const __scrollTo = window.scrollTo;

window.scrollTo = (...args) => {
  if (args[0] === 0 && args[1] === 0) {
    return __scrollTo.apply(window, args);
  }

  if (args.length === 1 && 'object' == typeof args[0]) {
    if (args[0].left === 0 && args[0].top === 0) {
      return __scrollTo.apply(window, args);
    }
  }

  console.debug('scrollTo() is intentionally broken to be noop.  See gatsby-browser.js');
};

// --- HubSpot contact form hydration on client navigation ---
const HUBSPOT_SCRIPT_MATCH = /(hsforms\.net|hs-scripts\.com|hscta\.net|hbspt\.)/i;

function replayHubspotScripts () {
  if (typeof document === 'undefined') return;

  const scripts = Array.from(document.body.querySelectorAll('script')).filter((script) => {
    if (script.dataset.hubspotReplayed === 'true') return false;
    if (script.closest('head')) return false;

    const src = script.src || '';
    const isHubspotSrc = HUBSPOT_SCRIPT_MATCH.test(src);
    const isHubspotInline = !script.src && HUBSPOT_SCRIPT_MATCH.test(script.textContent || '');
    return isHubspotSrc || isHubspotInline;
  });

  scripts.forEach((script) => {
    const clone = document.createElement('script');
    Array.from(script.attributes).forEach((attr) => clone.setAttribute(attr.name, attr.value));
    if (script.src) {
      clone.src = script.src;
      clone.async = clone.async || true;
      clone.defer = clone.defer || true;
    } else {
      clone.textContent = script.textContent;
    }
    script.dataset.hubspotReplayed = 'true';
    clone.dataset.hubspotReplayed = 'true';
    document.body.appendChild(clone);
  });
}

function hydrateHubspotForms () {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname || '';
  if (path.startsWith('/contact-form')) {
    replayHubspotScripts();
  }
}

export const onInitialClientRender = hydrateHubspotForms;
export const onRouteUpdate = hydrateHubspotForms;

// --- Push a GTM event on successful HubSpot form submission ---
function handleHubspotFormSubmission (ev) {
  const { formId, instanceId } = ev.detail;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'hubspotFormSubmit',
    'hs-formId': formId,
    'hs-instanceId': instanceId
  });
}

export const onClientEntry = () => {
  if (typeof window === 'undefined') return;
  window.addEventListener('hs-form-event:on-submission:success', handleHubspotFormSubmission);
};
